import React, { useState, useEffect } from 'react';
import CountryFlag, { countryFromRegion } from '../../../shared/components/CountryFlag';
import {
    Card,
    Table,
    Empty,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    message,
    Tooltip,
    Descriptions,
    List,
    Avatar,
    Alert
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
    ApartmentOutlined,
    CloudServerOutlined,
    NodeIndexOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import systemService from '../../services/systemService';
import L2Gate from './L2Gate';

const { Option } = Select;
const { TextArea } = Input;

const NodeGroups = () => {
    // 只在 CDN_L2_TOPOLOGY_ENABLED 真的关闭时提示(之前写死常显)
    const [l2Off, setL2Off] = useState(false);
    useEffect(() => {
        let alive = true;
        systemService.getRuntimeSettings()
            .then((r) => { if (alive && r && r.success) setL2Off(!r.data?.CDN_L2_TOPOLOGY_ENABLED?.value); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [form] = Form.useForm();

    // 套餐分组(CDN 分组):节点组可归属某个套餐分组,只对该套餐的域名生效(L2 适用范围)
    const [cdnGroups, setCdnGroups] = useState([]);
    useEffect(() => {
        loadGroups();
        cdnService.getCdnGroups().then((r) => { if (r && r.success) setCdnGroups(r.data || []); }).catch(() => {});
    }, []);

    const loadGroups = async () => {
        setLoading(true);
        try {
            const response = await cdnService.getNodeGroups();
            if (response.success) {
                setGroups(response.data || []);
            } else {
                message.error(response.error || '加载分组失败');
            }
        } catch (error) {
            message.error('加载分组失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingGroup(null);
        form.resetFields();
        form.setFieldsValue({
            status: 'active',
            cdn_group_id: 0
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingGroup(record);
        form.resetFields();
        form.setFieldsValue({
            name: record.name,
            description: record.description,
            region: record.region,
            status: record.status,
            cdn_group_id: record.cdn_group_id || 0,
            anycast_ipv4: record.anycast_ipv4 || '',
            anycast_ipv6: record.anycast_ipv6 || '',
            anycast_prefixes: (Array.isArray(record.anycast_prefixes) ? record.anycast_prefixes
                : (() => { try { return JSON.parse(record.anycast_prefixes || '[]'); } catch (e) { return []; } })()).join('\n'),
            anycast_extra_ips: (Array.isArray(record.anycast_extra_ips) ? record.anycast_extra_ips
                : (() => { try { return JSON.parse(record.anycast_extra_ips || '[]'); } catch (e) { return []; } })()).join('\n'),
        });
        setModalVisible(true);
    };

    const handleViewDetail = async (record) => {
        setSelectedGroup(record);
        // Load group detail with nodes
        try {
            const response = await cdnService.getNodeGroup(record.id);
            if (response.success) {
                setSelectedGroup(response.data);
            }
        } catch (error) {
            console.error('加载分组详情失败:', error);
        }
        setDetailModalVisible(true);
    };

    const handleCancel = () => {
        setModalVisible(false);
        form.resetFields();
        setEditingGroup(null);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: '确认删除',
            content: '确定要删除这个节点分组吗？删除后无法恢复。',
            okText: '确定',
            cancelText: '取消',
            okType: 'danger',
            onOk: async () => {
                try {
                    const response = await cdnService.deleteNodeGroup(id);
                    if (response.success) {
                        message.success('删除成功');
                        loadGroups();
                    } else {
                        message.error(response.error || '删除失败');
                    }
                } catch (error) {
                    message.error('删除失败: ' + error.message);
                }
            }
        });
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            const response = editingGroup
                ? await cdnService.updateNodeGroup({ ...values, id: editingGroup.id })
                : await cdnService.createNodeGroup(values);

            if (response.success) {
                message.success(editingGroup ? '更新成功' : '创建成功');
                setModalVisible(false);
                form.resetFields();
                setEditingGroup(null);
                loadGroups();
            } else {
                message.error(response.error || '操作失败');
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('请填写所有必填项');
            } else {
                message.error('操作失败: ' + error.message);
            }
        }
    };

    const columns = [
        {
            title: '分组名称',
            dataIndex: 'name',
            key: 'name',
            render: (text) => (
                <Space>
                    <ApartmentOutlined />
                    <strong>{text}</strong>
                </Space>
            )
        },
        {
            title: '描述',
            dataIndex: 'description',
            key: 'description',
            render: (text) => text || '-',
            ellipsis: true
        },
        {
            title: '地区',
            dataIndex: 'region',
            key: 'region',
            render: (text) => (text ? <><CountryFlag code={countryFromRegion(text)} />{text}</> : '-')
        },
        {
            title: '适用套餐',
            dataIndex: 'cdn_group_id',
            key: 'cdn_group_id',
            render: (gid) => {
                if (!gid) return <Tag>全平台共用</Tag>;
                const g = cdnGroups.find((x) => x.id === gid);
                return <Tag color="blue">{g ? g.name : `#${gid}`}</Tag>;
            }
        },
        {
            title: '节点数',
            dataIndex: 'nodes_count',
            key: 'nodes_count',
            render: (count, record) => (
                <Space>
                    <Tag icon={<CloudServerOutlined />}>{count || 0} 节点</Tag>
                    {record.l2_nodes_count > 0 && (
                        <Tag icon={<NodeIndexOutlined />} color="blue">{record.l2_nodes_count} L2</Tag>
                    )}
                </Space>
            )
        },
        {
            title: '活跃节点',
            dataIndex: 'active_nodes_count',
            key: 'active_nodes_count',
            render: (count, record) => (
                <Tag color={count > 0 ? 'success' : 'default'}>
                    {count || 0} / {record.nodes_count || 0}
                </Tag>
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'active' ? 'success' : 'default'}>
                    {status === 'active' ? '活跃' : '未激活'}
                </Tag>
            )
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="查看详情">
                        <Button
                            type="link"
                            size="small"
                            icon={<InfoCircleOutlined />}
                            onClick={() => handleViewDetail(record)}
                        />
                    </Tooltip>
                    <Tooltip title="编辑">
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Tooltip title="删除">
                        <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record.id)}
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <div>
            {l2Off && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="L2 未启用：分组/L2 标记暂不下发到边缘（CDN_L2_TOPOLOGY_ENABLED=0）"
                />
            )}
            <Card
                title="CDN节点分组管理"
                extra={
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadGroups}
                            loading={loading}
                        >
                            刷新
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAdd}
                        >
                            添加分组
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={groups}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 个分组`
                    }}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: <Empty description="暂无分组，点击右上角新建分组" /> }}
                />
            </Card>

            {/* 添加/编辑分组弹窗 */}
            <Modal
                title={editingGroup ? '编辑节点分组' : '添加节点分组'}
                open={modalVisible}
                onCancel={handleCancel}
                onOk={handleSubmit}
                okText="确定"
                cancelText="取消"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="分组名称"
                        name="name"
                        rules={[{ required: true, message: '请输入分组名称' }]}
                    >
                        <Input placeholder="例如: asia-l2-group" />
                    </Form.Item>

                    <Form.Item
                        label="描述"
                        name="description"
                    >
                        <TextArea rows={2} placeholder="分组描述（可选）" />
                    </Form.Item>

                    <Form.Item
                        label="地区"
                        name="region"
                    >
                        <Select placeholder="选择地区（可选）" allowClear>
                            <Option value="global">全球</Option>
                            <Option value="cn-north">华北</Option>
                            <Option value="cn-east">华东</Option>
                            <Option value="cn-south">华南</Option>
                            <Option value="cn-west">西南</Option>
                            <Option value="us-west">美西</Option>
                            <Option value="us-east">美东</Option>
                            <Option value="eu">欧洲</Option>
                            <Option value="ap">亚太</Option>
                            <Option value="Asia">亚洲</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="适用套餐"
                        name="cdn_group_id"
                        tooltip="选了某个套餐分组：组里的 L2 只对该套餐的域名生效；全平台共用：对所有套餐生效"
                    >
                        <Select
                            options={[{ value: 0, label: '全平台共用' }, ...cdnGroups.map((g) => ({ value: g.id, label: g.name }))]}
                        />
                    </Form.Item>

                    <Form.Item
                        label="状态"
                        name="status"
                        initialValue="active"
                    >
                        <Select>
                            <Option value="active">活跃</Option>
                            <Option value="inactive">未激活</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item label="Anycast IPv4" name="anycast_ipv4" tooltip="集群共用的 anycast 地址；配置后 DNS 优先返回它（需节点支持 BGP）">
                        <Input placeholder="可选" />
                    </Form.Item>
                    <Form.Item label="Anycast IPv6" name="anycast_ipv6">
                        <Input placeholder="可选" />
                    </Form.Item>
                    <Form.Item label="附加 Anycast IP" name="anycast_extra_ips" tooltip="同网段内的其他 anycast 地址，每行一个；宣告节点都会绑上，可放进 IP 子池按套餐分开用">
                        <Input.TextArea rows={2} placeholder={'203.0.113.2\n203.0.113.3'} />
                    </Form.Item>
                    <Form.Item label="宣告网段" name="anycast_prefixes" tooltip="BIRD 宣告的网段，每行一个（IPv4 至少 /24，IPv6 至少 /48）">
                        <Input.TextArea rows={2} placeholder={'203.0.113.0/24\n2001:db8::/48'} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 分组详情弹窗 */}
            <Modal
                title={
                    <Space>
                        <ApartmentOutlined />
                        <span>分组详情 - {selectedGroup?.name}</span>
                    </Space>
                }
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={null}
                width={700}
            >
                {selectedGroup && (
                    <>
                        <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="分组名称">{selectedGroup.name}</Descriptions.Item>
                            <Descriptions.Item label="状态">
                                <Tag color={selectedGroup.status === 'active' ? 'success' : 'default'}>
                                    {selectedGroup.status === 'active' ? '活跃' : '未激活'}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="地区"><CountryFlag code={countryFromRegion(selectedGroup.region)} />{selectedGroup.region || '-'}</Descriptions.Item>
                            <Descriptions.Item label="节点数">{selectedGroup.nodes_count || 0}</Descriptions.Item>
                            <Descriptions.Item label="描述" span={2}>{selectedGroup.description || '-'}</Descriptions.Item>
                        </Descriptions>

                        <Card title="分组节点" size="small">
                            {selectedGroup.nodes && selectedGroup.nodes.length > 0 ? (
                                <List
                                    dataSource={selectedGroup.nodes}
                                    renderItem={node => (
                                        <List.Item>
                                            <List.Item.Meta
                                                avatar={<Avatar icon={<CloudServerOutlined />} style={{ backgroundColor: node.is_l2 ? '#1890ff' : '#87d068' }} />}
                                                title={
                                                    <Space>
                                                        {node.name}
                                                        {node.is_l2 && <Tag color="blue">L2</Tag>}
                                                        <Tag color={node.status === 'active' ? 'success' : 'default'}>
                                                            {node.status === 'active' ? '活跃' : '未激活'}
                                                        </Tag>
                                                    </Space>
                                                }
                                                description={`IP: ${node.ip}`}
                                            />
                                        </List.Item>
                                    )}
                                />
                            ) : (
                                <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>
                                    暂无节点
                                </div>
                            )}
                        </Card>
                    </>
                )}
            </Modal>
        </div>
    );
};

const NodeGroupsGated = () => <L2Gate title="CDN L2 分组"><NodeGroups /></L2Gate>;

export default NodeGroupsGated;
